import React from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import { formatNumberToLocalePrice } from "../../../../utils/NumberHelpers";
import { PredefinedIconType } from "../../../shared/icons/Icon/Predefined";
import LinkComponent, { navigate } from "../../../shared/LinkComponent";
import Button from "../../../shared/NewForms/Button";
import Headline from "../../../shared/NewHeadline";
import defaultImg from "../Img/platzhalter_technikshop.png";
import useStore from "../store";

const LocalContainer = styled.div`
    margin-top: 20px;
    ${({ theme }) => css`
        ${theme.paddings.containerOuter.left}
        ${theme.paddings.containerOuter.right}
    `}

    color: ${({ theme }) => theme.colors.blue.toString()};
`;

const Container = styled.div`
    margin-bottom: 50px;
    position: relative;
    display: flex;
    flex-direction: column;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        flex-direction: row;
        gap: 50px;
        margin-bottom: 0;
    `}
`;

const Span = styled.span`
    font-size: small;
    font-weight: normal;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 50px;
    width: 100%;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        gap: 50px;
        width: 80%;
    `}
`;

const ProductContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const Image = styled.img`
    width: 150px;
    height: 150px;
    object-fit: cover;
`;

const DeleteButton = styled.button`
    align-self: end;
    width: fit-content;
    padding: 0;
    margin: 0;
    color: ${({ theme }) => theme.colors.darkGrey.toString()};
    text-decoration: underline;
    cursor: pointer;
    background-color: transparent;
    border: none;
`;

const Row = styled.div`
    display: flex;
    gap: 20px;
    align-items: start;
`;

const SavedProducts = () => {
    const theme = useTheme();
    const [savedProducts, deleteProductFromSaved, getMainImage] = useStore(
        (state) => [
            state.savedProducts,
            state.deleteProductFromSaved,
            state.getMainImage,
        ],
        shallow
    );

    return (
        <LocalContainer>
            <LinkComponent
                goBack
                link={{ text: "Zurück", href: "/technik/produkte" }}
                icon={PredefinedIconType.arrowLeft}
                leftIcon
                color="red"
            />
            <Headline
                css={css`
                    margin: 50px 0 20px;
                `}
            >
                Meine Merkliste <Span>({savedProducts.length} Artikel)</Span>
            </Headline>
            <Container>
                {savedProducts.length > 0 ? (
                    <FlexContainer>
                        {savedProducts.map((product) => (
                            <ProductContainer key={product.articleNumber}>
                                <DeleteButton
                                    onClick={() =>
                                        deleteProductFromSaved(product)
                                    }
                                >
                                    Löschen
                                </DeleteButton>
                                <Row
                                    css={css`
                                        cursor: pointer;
                                    `}
                                    onClick={() =>
                                        navigate(
                                            `/technik/product/${product.articleNumber}/`
                                        )
                                    }
                                >
                                    <Image
                                        src={
                                            getMainImage(product) ?? defaultImg
                                        }
                                        alt=""
                                    />
                                    <div>
                                        <div
                                            css={css`
                                                font-weight: bold;
                                            `}
                                        >
                                            {product.name}
                                        </div>
                                        <div>
                                            {product.quantityUnit
                                                .toString()
                                                .replace(".00", "")}{" "}
                                            {product.packagingUnit}
                                        </div>
                                        <div
                                            css={css`
                                                font-weight: bold;
                                                color: ${theme.colors.red.toString()};
                                            `}
                                        >
                                            {product.listPrice
                                                ? `${product.listPrice} €`
                                                : "Preis auf Anfrage"}
                                        </div>
                                        <div>exkl. MwSt.</div>
                                        {product.shippingCosts && (
                                            <div>
                                                zzgl. Versandkosten{" "}
                                                {formatNumberToLocalePrice(
                                                    product.shippingCosts
                                                )}{" "}
                                                € netto
                                            </div>
                                        )}
                                    </div>
                                </Row>
                            </ProductContainer>
                        ))}
                    </FlexContainer>
                ) : (
                    <FlexContainer>
                        <p>Du hast keine Artikel in Deiner Merkliste</p>
                    </FlexContainer>
                )}
                <div>
                    <Headline>Produkte Anfragen</Headline>
                    <p>
                        Du kannst hier eine Anfrage stellen. Wir werden Dir so
                        schnell es geht ein Angebot schicken.
                    </p>
                    <Button
                        onClick={() => navigate("/technik/anfrage")}
                        fullWidth
                        disabled={savedProducts.length <= 0}
                    >
                        Anfrage senden
                    </Button>
                </div>
            </Container>
        </LocalContainer>
    );
};

export default SavedProducts;
